import React, { useState, useEffect } from 'react';
import {
  FaCreditCard, FaCrown, FaCompass, FaCalendarAlt, FaCheckCircle,
  FaTimesCircle, FaExclamationCircle, FaRocket, FaArrowLeft,
  FaUser, FaGlobe, FaUsers, FaBriefcase, FaBuilding,
  FaGift, FaStar
} from 'react-icons/fa';
import { createBillingPortalSession, reactivateSubscription, getPointsBalance } from '../api/client';
import CancellationModal from './CancellationModal';
import VoiceSettings from './voice/VoiceSettings';

const TIER_CONFIG = {
  demo: {
    name: 'Demo Account',
    icon: FaCompass,
    style: { background: '#EBE7E3' },
    iconStyle: { color: '#8A827A' },
    iconBg: { background: '#DDD8D2' },
    titleStyle: { color: '#262420' },
    descStyle: { color: '#8A827A' },
    description: 'No active subscription',
    isPaid: false,
    features: [
      { text: 'View existing itineraries', included: true },
      { text: 'Purchase trip credits (PAYG)', included: true },
      { text: 'AI-powered route planning', included: false },
      { text: 'AI-powered modifications', included: false },
      { text: 'Export to PDF', included: false },
      { text: 'Trip photos', included: false },
      { text: 'Expense tracking', included: false },
    ]
  },
  free: {
    name: 'Free Account',
    icon: FaCompass,
    style: { background: '#EBE7E3' },
    iconStyle: { color: '#8A827A' },
    iconBg: { background: '#DDD8D2' },
    titleStyle: { color: '#262420' },
    descStyle: { color: '#8A827A' },
    description: 'No active subscription',
    isPaid: false,
    features: [
      { text: 'View existing itineraries', included: true },
      { text: 'Purchase trip credits (PAYG)', included: true },
      { text: 'AI-powered route planning', included: false },
      { text: 'AI-powered modifications', included: false },
      { text: 'Export to PDF', included: false },
      { text: 'Trip photos', included: false },
      { text: 'Expense tracking', included: false },
    ]
  },
  individual: {
    name: 'Solo Plan',
    icon: FaUser,
    style: { background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)' },
    iconStyle: { color: 'white' },
    iconBg: { background: 'rgba(255,255,255,0.2)' },
    titleStyle: { color: 'white' },
    descStyle: { color: 'rgba(255,255,255,0.8)' },
    description: 'Unlimited itineraries',
    isPaid: true,
    features: [
      { text: 'Unlimited itineraries', included: true },
      { text: 'AI-powered modifications', included: true },
      { text: 'AI Chat assistant', included: true },
      { text: 'Export to PDF', included: true },
      { text: 'Trip photos & attachments', included: true },
      { text: 'Offline access', included: true },
      { text: 'Expense tracking & splitting', included: false },
      { text: 'Invite members / Group Chat', included: false },
      { text: 'Receipt scanning', included: false },
    ]
  },
  group: {
    name: 'Group Plan',
    icon: FaUsers,
    style: { background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)' },
    iconStyle: { color: 'white' },
    iconBg: { background: 'rgba(255,255,255,0.2)' },
    titleStyle: { color: 'white' },
    descStyle: { color: 'rgba(255,255,255,0.8)' },
    description: 'Up to 6 members',
    isPaid: true,
    features: [
      { text: 'Unlimited itineraries', included: true },
      { text: 'Group planning (up to 6 members)', included: true },
      { text: 'AI-powered modifications', included: true },
      { text: 'Expense tracking & splitting', included: true },
      { text: 'Group Chat', included: true },
      { text: 'Receipt scanning', included: true },
      { text: 'Export to PDF', included: true },
      { text: 'Trip photos & attachments', included: true },
      { text: 'Priority support', included: true },
    ]
  },
  starter_agent: {
    name: 'Starter Agent',
    icon: FaBriefcase,
    style: { background: 'linear-gradient(135deg, #0D9488 0%, #0F766E 100%)' },
    iconStyle: { color: 'white' },
    iconBg: { background: 'rgba(255,255,255,0.2)' },
    titleStyle: { color: 'white' },
    descStyle: { color: 'rgba(255,255,255,0.8)' },
    description: '10 trips per month',
    isPaid: true,
    features: [
      { text: '10 trips/month (+$2/ea overage)', included: true },
      { text: 'Group trip planning', included: true },
      { text: 'AI-powered modifications', included: true },
      { text: 'Expense tracking & splitting', included: true },
      { text: 'Receipt scanning', included: true },
      { text: 'Export to PDF', included: true },
      { text: 'Trip photos & attachments', included: true },
      { text: 'Priority support', included: true },
    ]
  },
  professional_agent: {
    name: 'Professional Agent',
    icon: FaBuilding,
    style: { background: 'linear-gradient(135deg, #4F46E5 0%, #4338CA 100%)' },
    iconStyle: { color: 'white' },
    iconBg: { background: 'rgba(255,255,255,0.2)' },
    titleStyle: { color: 'white' },
    descStyle: { color: 'rgba(255,255,255,0.8)' },
    description: '40 trips per month',
    isPaid: true,
    features: [
      { text: '40 trips/month (+$1.50/ea overage)', included: true },
      { text: 'Everything in Starter Agent', included: true },
      { text: 'Advanced analytics', included: true },
      { text: 'Dedicated support', included: true },
    ]
  },
  enterprise_agency: {
    name: 'Enterprise',
    icon: FaGlobe,
    style: { background: 'linear-gradient(135deg, #EA580C 0%, #C2410C 100%)' },
    iconStyle: { color: 'white' },
    iconBg: { background: 'rgba(255,255,255,0.2)' },
    titleStyle: { color: 'white' },
    descStyle: { color: 'rgba(255,255,255,0.8)' },
    description: 'Unlimited trips',
    isPaid: true,
    features: [
      { text: 'Unlimited trips', included: true },
      { text: 'Everything in Professional', included: true },
      { text: 'White-label branding', included: true },
      { text: 'Custom integrations', included: true },
      { text: 'Dedicated account manager', included: true },
    ]
  },
  unlimited: {
    name: 'Unlimited Plan',
    icon: FaCrown,
    style: { background: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)' },
    iconStyle: { color: 'white' },
    iconBg: { background: 'rgba(255,255,255,0.3)' },
    titleStyle: { color: 'white' },
    descStyle: { color: 'rgba(255,255,255,0.9)' },
    description: 'Unlimited access',
    isPaid: false,
    features: [
      { text: 'Unlimited itineraries', included: true },
      { text: 'Group trip planning (10 members)', included: true },
      { text: 'AI-powered modifications', included: true },
      { text: 'Export to PDF', included: true },
      { text: 'Trip photos', included: true },
      { text: 'Expense tracking', included: true },
      { text: 'Premium support', included: true },
      { text: 'Early access to features', included: true },
    ]
  },
  admin: {
    name: 'Admin Plan',
    icon: FaCrown,
    style: { background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)' },
    iconStyle: { color: 'white' },
    iconBg: { background: 'rgba(255,255,255,0.3)' },
    titleStyle: { color: 'white' },
    descStyle: { color: 'rgba(255,255,255,0.9)' },
    description: 'Unlimited access',
    isPaid: false,
    isAdmin: true,
    features: [
      { text: 'Unlimited itineraries', included: true },
      { text: 'Unlimited group members', included: true },
      { text: 'AI-powered modifications', included: true },
      { text: 'Export to PDF', included: true },
      { text: 'Trip photos', included: true },
      { text: 'Expense tracking', included: true },
      { text: 'Premium support', included: true },
      { text: 'Early access to features', included: true },
    ]
  },
  promotional: {
    name: 'Early Adopter Special',
    icon: FaGift,
    style: { background: 'linear-gradient(135deg, #F97316 0%, #EF4444 100%)' },
    iconStyle: { color: 'white' },
    iconBg: { background: 'rgba(255,255,255,0.3)' },
    titleStyle: { color: 'white' },
    descStyle: { color: 'rgba(255,255,255,0.9)' },
    description: 'Promotional access (sunset)',
    isPaid: false,
    isPromotional: true,
    features: [
      { text: 'Unlimited itineraries', included: true },
      { text: 'Group trip planning (10 members)', included: true },
      { text: 'AI-powered modifications', included: true },
      { text: 'Export to PDF', included: true },
      { text: 'Trip photos & attachments', included: true },
      { text: 'Expense tracking', included: true },
      { text: 'Offline access', included: true },
      { text: 'Priority support', included: true },
      { text: 'Early access to features', included: true },
    ]
  }
};

const Account = ({ user, onBack, onViewPricing, onSubscriptionCancelled, onViewTravelPoints }) => {
  const [billingLoading, setBillingLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [freshPoints, setFreshPoints] = useState(null);

  useEffect(() => {
    if (onViewTravelPoints) {
      getPointsBalance()
        .then(data => setFreshPoints(data))
        .catch(() => {});
    }
  }, [onViewTravelPoints]);

  const tier = user?.subscription_tier || 'free';
  const tierConfig = TIER_CONFIG[tier] || TIER_CONFIG.free;
  const isPaidTier = tierConfig.isPaid || false;
  const isPromotional = tierConfig.isPromotional || false;
  const TierIcon = tierConfig.icon;

  const handleManageBilling = async () => {
    setBillingLoading(true);
    setError('');
    try {
      const { portal_url } = await createBillingPortalSession();
      window.location.href = portal_url;
    } catch (err) {
      console.error('Error opening billing portal:', err);
      setError('Failed to open billing portal. Please try again.');
      setBillingLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: {
        icon: FaCheckCircle,
        style: { color: '#3F7050', backgroundColor: '#D0E5D6' },
        label: 'Active'
      },
      canceled: {
        icon: FaTimesCircle,
        style: { color: '#dc2626', backgroundColor: '#fee2e2' },
        label: 'Canceled'
      },
      cancelling: {
        icon: FaExclamationCircle,
        style: { color: '#d97706', backgroundColor: '#fef3c7' },
        label: 'Cancelling at Period End'
      },
      past_due: {
        icon: FaExclamationCircle,
        style: { color: '#D5945C', backgroundColor: '#FDF3E8' },
        label: 'Past Due'
      },
      trialing: {
        icon: FaCheckCircle,
        style: { color: '#766888', backgroundColor: '#D9D4E1' },
        label: 'Trial'
      }
    };

    const config = statusConfig[status] || statusConfig.active;
    const Icon = config.icon;

    return (
      <span
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium"
        style={config.style}
      >
        <Icon className="text-xs" />
        {config.label}
      </span>
    );
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={onBack}
          className="p-2 rounded-lg transition-colors"
          style={{ backgroundColor: 'transparent' }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#EBE7E3'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <FaArrowLeft style={{ color: '#68625A' }} />
        </button>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#262420' }}>Account</h1>
          <p style={{ color: '#8A827A' }}>Manage your subscription and billing</p>
        </div>
      </div>

      {/* Current Plan Card */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
        <div className="p-6" style={tierConfig.style}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={tierConfig.iconBg}>
                <TierIcon className="text-2xl" style={tierConfig.iconStyle} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider mb-0.5" style={{ ...tierConfig.descStyle, opacity: 0.7 }}>
                  Current Plan
                </p>
                <h2 className="text-xl font-bold" style={tierConfig.titleStyle}>
                  {tierConfig.name}
                </h2>
                <p className="text-sm" style={tierConfig.descStyle}>
                  {tierConfig.description}
                </p>
              </div>
            </div>
            {isPaidTier && user?.subscription_status && (
              <div>
                {getStatusBadge(user.subscription_status)}
              </div>
            )}
          </div>
        </div>

        <div className="p-6 space-y-4">
          {isPromotional ? (
            <>
              {/* Promotional Subscription Details */}
              <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 text-orange-700 mb-2">
                  <FaGift className="text-lg" />
                  <span className="font-bold">Early Adopter Promotional Offer</span>
                </div>
                <p className="text-sm text-orange-600">
                  You're enjoying full premium access for FREE! Thank you for being an early adopter.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-xl p-4" style={{ backgroundColor: '#F5F3F1' }}>
                  <div className="flex items-center gap-2 text-sm mb-1" style={{ color: '#8A827A' }}>
                    <FaCalendarAlt />
                    <span>Started</span>
                  </div>
                  <p className="text-lg font-semibold" style={{ color: '#262420' }}>
                    {formatDate(user?.subscription_start)}
                  </p>
                </div>

                <div className="rounded-xl p-4" style={{ backgroundColor: '#F5F3F1' }}>
                  <div className="flex items-center gap-2 text-sm mb-1" style={{ color: '#8A827A' }}>
                    <FaGift />
                    <span>Plan Type</span>
                  </div>
                  <p className="text-lg font-semibold" style={{ color: '#262420' }}>
                    6-Month Free Trial
                  </p>
                </div>
              </div>

              {user?.subscription_end && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-blue-700">
                    <FaCalendarAlt />
                    <span className="font-medium">
                      Premium access until {formatDate(user.subscription_end)}
                    </span>
                  </div>
                  <p className="text-sm text-blue-600 mt-1">
                    After this date, you'll be moved to the Free plan unless you upgrade.
                  </p>
                </div>
              )}

              {/* Upgrade button for promotional users */}
              <button
                onClick={onViewPricing}
                className="w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all"
                style={{ background: 'linear-gradient(135deg, #877B98 0%, #5C9B6E 100%)', color: 'white' }}
              >
                <FaRocket />
                Upgrade Plan
              </button>
            </>
          ) : isPaidTier ? (
            <>
              {/* Subscription Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-xl p-4" style={{ backgroundColor: '#F5F3F1' }}>
                  <div className="flex items-center gap-2 text-sm mb-1" style={{ color: '#8A827A' }}>
                    <FaCalendarAlt />
                    <span>Subscription Started</span>
                  </div>
                  <p className="text-lg font-semibold" style={{ color: '#262420' }}>
                    {formatDate(user?.subscription_start)}
                  </p>
                </div>

                <div className="rounded-xl p-4" style={{ backgroundColor: '#F5F3F1' }}>
                  <div className="flex items-center gap-2 text-sm mb-1" style={{ color: '#8A827A' }}>
                    <FaCreditCard />
                    <span>Billing Cycle</span>
                  </div>
                  <p className="text-lg font-semibold capitalize" style={{ color: '#262420' }}>
                    {user?.billing_cycle || 'Monthly'}
                  </p>
                </div>
              </div>

              {user?.subscription_end && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-yellow-700">
                    <FaExclamationCircle />
                    <span className="font-medium">
                      {user?.subscription_status === 'canceled'
                        ? `Access until ${formatDate(user.subscription_end)}`
                        : user?.subscription_status === 'cancelling'
                          ? `Cancels on ${formatDate(user.subscription_end)}`
                          : `Renews on ${formatDate(user.subscription_end)}`
                      }
                    </span>
                  </div>
                </div>
              )}

              {/* Manage Billing Button */}
              <button
                onClick={handleManageBilling}
                disabled={billingLoading}
                className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                style={{ backgroundColor: '#766888', color: 'white' }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#625674'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#766888'}
              >
                <FaCreditCard />
                {billingLoading ? 'Opening Billing Portal...' : 'Manage Billing'}
              </button>

              {/* Reactivate Button (when cancelling) */}
              {user?.subscription_status === 'cancelling' && (
                <button
                  onClick={async () => {
                    try {
                      await reactivateSubscription();
                      onSubscriptionCancelled(); // reuses the refresh handler
                    } catch (err) {
                      setError(err.response?.data?.detail || 'Failed to reactivate subscription.');
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2 bg-green-500 text-white py-3 px-6 rounded-xl font-medium hover:bg-green-600 transition-all"
                >
                  <FaCheckCircle />
                  Reactivate Subscription
                </button>
              )}

              {/* Cancel Subscription Button */}
              {user?.subscription_status !== 'canceled' && user?.subscription_status !== 'cancelling' && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="w-full flex items-center justify-center gap-2 border border-red-300 text-red-600 py-3 px-6 rounded-xl font-medium hover:bg-red-50 transition-all"
                >
                  <FaTimesCircle />
                  Cancel Subscription
                </button>
              )}

              {error && (
                <p className="text-red-600 text-sm text-center">{error}</p>
              )}
            </>
          ) : (
            <>
              {/* Free Plan Info */}
              <div className="text-center py-4">
                <p className="mb-4" style={{ color: '#68625A' }}>
                  You're on the Free plan. Upgrade to get unlimited itineraries,
                  group planning, and premium features.
                </p>
                <button
                  onClick={onViewPricing}
                  className="inline-flex items-center gap-2 py-3 px-8 rounded-xl font-medium hover:shadow-lg hover:scale-105 transition-all"
                  style={{ background: 'linear-gradient(135deg, #877B98 0%, #5C9B6E 100%)', color: 'white' }}
                >
                  <FaRocket />
                  View Plans
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Travel Points Card */}
      {onViewTravelPoints && (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
          <div className="p-6" style={{ background: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(16, 185, 129, 0.2)' }}>
                  <FaStar className="text-2xl" style={{ color: '#10B981' }} />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-0.5" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    Travel Points
                  </p>
                  <p className="text-2xl font-bold text-emerald-400">
                    {(freshPoints?.balance ?? user?.travel_points ?? 0).toLocaleString()} <span className="text-sm text-gray-400">pts</span>
                  </p>
                </div>
              </div>
              <span className="px-3 py-1 rounded-full text-xs font-medium capitalize" style={{
                backgroundColor: 'rgba(255,255,255,0.15)', color: 'white'
              }}>
                {freshPoints?.tier_name || user?.points_tier || 'explorer'}
              </span>
            </div>
          </div>
          <div className="p-4">
            <button
              onClick={onViewTravelPoints}
              className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-medium transition-all hover:shadow-md"
              style={{ backgroundColor: '#877B98', color: 'white' }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#625674'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#877B98'}
            >
              <FaStar />
              View Travel Points & Rewards
            </button>
          </div>
        </div>
      )}

      {/* Plan Features */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-lg font-semibold mb-4" style={{ color: '#262420' }}>
          Your {tierConfig.name.replace(' Plan', '')} Benefits
        </h3>
        <div className="space-y-3">
          {tierConfig.features.map((feature, idx) => (
            <FeatureItem key={idx} text={feature.text} included={feature.included} />
          ))}
        </div>
      </div>

      {/* Voice Settings */}
      <div className="mt-6">
        <VoiceSettings />
      </div>

      {/* Upgrade Prompt for non-top tier (hide for top-tier plans) */}
      {!['enterprise_agency', 'unlimited', 'admin'].includes(tier) && (
        <div
          className="mt-6 rounded-2xl p-6"
          style={{ background: 'linear-gradient(135deg, #ECEAF0 0%, #E8F2EB 100%)', border: '1px solid #D9D4E1' }}
        >
          <div className="flex items-center gap-3 mb-4">
            <FaGlobe className="text-2xl" style={{ color: '#877B98' }} />
            <div>
              <h3 className="font-semibold" style={{ color: '#262420' }}>Want more?</h3>
              <p className="text-sm" style={{ color: '#68625A' }}>
                Upgrade to unlock more itineraries and premium features.
              </p>
            </div>
          </div>
          <button
            onClick={onViewPricing}
            className="w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all"
            style={{ background: 'linear-gradient(135deg, #877B98 0%, #5C9B6E 100%)', color: 'white' }}
          >
            <FaRocket />
            Upgrade Plan
          </button>
        </div>
      )}

      {/* Cancellation Modal */}
      <CancellationModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onCancelled={onSubscriptionCancelled}
        user={user}
      />
    </div>
  );
};

const FeatureItem = ({ text, included }) => (
  <div className="flex items-center gap-3">
    {included ? (
      <FaCheckCircle className="flex-shrink-0" style={{ color: '#5C9B6E' }} />
    ) : (
      <FaTimesCircle className="flex-shrink-0" style={{ color: '#CCC5BD' }} />
    )}
    <span style={{ color: included ? '#48433D' : '#ADA59B' }}>
      {text}
    </span>
  </div>
);

export default Account;
